// opervex-frontend/src/pages/Financial/Entry.js

import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Container, Form, InputGroup, Row, Tab, Table, Tabs } from "react-bootstrap";
import { FaPlus, FaSave, FaSearch, FaPencilAlt, FaTrash } from "react-icons/fa";
import { MdOutlineCancel } from "react-icons/md";
import { useParams } from "react-router-dom";

import { SearchHelpModal } from "../../utils/SearchHelp";
import ConfirmService from "../../components/ConfirmationModal/ConfirmService";
import ClearingModalService from "../../components/Financial/ClearingModalService";
import CurrencyInput from "../../components/CurrencyInput";

import useCommandActions, { DEFAULT_ACTIONS } from "../../hooks/useCommandActions";
import { useAuth } from "../../contexts/AuthContext";
import useOpervex from "../../hooks/useOpervex";

const rolePageRequire = "FINANCIAL_ENTRY";

const today = () => new Date().toISOString().slice(0, 10);

const newDocument = () => ({
  id: 0,

  accountType: "",
  branchId: 0,

  entryOrigin: "",
  documentType: "",
  documentNumber: "",
  referenceKey: "",

  documentDate: "",
  postingDate: "",
  dueDate: "",

  currency: "BRL",

  originalAmount: 0,
  interestAmount: 0,
  fineAmount: 0,
  discountAmount: 0,
  totalAmount: 0,

  isReceipt: false,
  receivedOn: "",
  receiptAccount: "",
  receiptReference: "",
  isPayment: false,
  paidOn: "",
  paymentAccount: "",
  paymentReference: "",

  categoryId: "",
  costCenterId: "",

  notes: "",

  // Baixas / Compensações
  clearings: [],
});

const periodFrom = (date) => {
  if (!date) return "";

  const [year, month] = date.split("-");

  return month && year ? `${month}/${year}` : "";
};

const costCenterCode = (costCenter, fallback) => {
  const typedCode = String(fallback ?? "").trim();
  const highLevel = costCenter?.highLevel;
  const codeLevel = costCenter?.codeLevel;

  // O código é textual: se ele foi digitado com zero à esquerda, esse zero é significativo.
  if (typedCode.startsWith("0")) {
    return typedCode;
  }

  if (codeLevel !== undefined && codeLevel !== null && codeLevel !== "") {
    return `${highLevel || ""}${codeLevel}`;
  }

  return typedCode || String(costCenter?.id ?? "").trim();
};

const FinancialEntry = () => {
  const { loadingGlobal } = useAuth();
  const { actions, commandAction } = useCommandActions(rolePageRequire);

  const { id } = useParams();
  const api = useOpervex();

  const [isCreate, setIsCreate] = useState(false);
  const [editing, setEditing] = useState(false);
  const [data, setData] = useState(newDocument());

  const [branches, setBranches] = useState([]);
  const [categories, setCategories] = useState([]);
  const [costCenters, setCostCenters] = useState([]);

  useEffect(() => {
    (async () => {
      const [branchList, categoryList] = await Promise.all([api.Accounting.Branches.findAll(), api.Financial.Categories.findAll()]);

      setBranches(branchList || []);
      setCategories(categoryList || []);

      if (id) {
        load(id);
      } else {
        commandAction({ ...DEFAULT_ACTIONS, create: true });
      }
    })();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================================
  // Valores
  // ============================================================

  const totalAmount = useMemo(
    () =>
      Number(data.originalAmount || 0) +
      Number(data.interestAmount || 0) +
      Number(data.fineAmount || 0) -
      Number(data.discountAmount || 0),
    [data.originalAmount, data.interestAmount, data.fineAmount, data.discountAmount],
  );

  const associatedAmount = useMemo(
    () =>
      (data.compensatedParents ?? []).reduce(
        (total, parent) => total + Number(parent.totalAmount || 0),
        0,
      ),
    [data.compensatedParents],
  );

  const clearedAmount = useMemo(
    () => (data.clearings ?? []).reduce((total, clearing) => total + Number(clearing.totalAmount || clearing.amount || 0), 0),
    [data.clearings],
  );

  const balanceAmount = useMemo(
    () => Math.max(totalAmount - clearedAmount - associatedAmount, 0),
    [totalAmount, clearedAmount, associatedAmount],
  );

  const financialStatus = useMemo(() => {
    if (data.isReceipt && data.receivedOn) {
      if (balanceAmount <= 0) return "DISTRIBUTED";
      if (associatedAmount !== 0) return "RECEIVED_PARTIAL";
      return "RECEIVED_AVAILABLE";
    }

    if (data.isPayment && data.paidOn) {
      if (balanceAmount <= 0) return "PAID_DISTRIBUTED";
      if (associatedAmount !== 0) return "PAID_PARTIAL";
      return "PAID_AVAILABLE";
    }

    if (clearedAmount + associatedAmount <= 0) {
      return "OPEN";
    }

    if (balanceAmount > 0) {
      return "PARTIAL";
    }

    return "CLEARED";
  }, [data.isReceipt, data.receivedOn, data.isPayment, data.paidOn, clearedAmount, associatedAmount, balanceAmount]);

  if (loadingGlobal) {
    return <div>Carregando...</div>;
  }

  // ============================================================
  // Load
  // ============================================================

  const load = async (recordId) => {
    setEditing(false);
    setIsCreate(false);

    if (!recordId) return;

    const result = await api.Financial.Entries.findId(recordId);
    if (result?.errors) return await ConfirmService.show("Aviso", result.errors[0]?.message || "Documento não encontrado", true);

    if (result?.id) commandAction({ ...DEFAULT_ACTIONS, create: true, change: true });
    else commandAction({ ...DEFAULT_ACTIONS, create: true });

    setData({
      ...result,
      clearings: result.clearings || [],
    });
  };

  // ============================================================
  // Update data object using screen
  // ============================================================

  const change = (event) => {
    const { name, value, type, checked } = event.target;

    setData((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // ============================================================
  // Handle Search Cost Center
  // ============================================================

  const handleSearchCostCenter = async () => {
    if (!editing) return;

    const item = await SearchHelpModal({
      urlData: "/Accounting/CostCenters/SearchHelp",
      schema: await api.Accounting.CostCenters.schema(),
    });

    if (item) {
      validateCostCenter(item.id);
    }
  };

  // ============================================================
  // Validate Cost Center
  // ============================================================

  const validateCostCenter = async (id) => {
    let costCenterId = "";
    const enteredCode = String(id ?? "").trim();

    const costCenterCheck = costCenters.find((x) => String(x.id) === enteredCode);

    if (costCenterCheck) {
      if (!costCenterCheck.disabled) {
        costCenterId = String(costCenterCheck.id);
      }
    } else {
      const result = await api.Accounting.CostCenters.findId(enteredCode);

      if (result) {
        if (!result.disabled) {
          costCenterId = costCenterCode(result, enteredCode);
        }

        const newCostCenter = {
          id: costCenterCode(result, enteredCode),
          description: result.description,
          disabled: result.disabled,
        };

        if (costCenters.findIndex((x) => String(x.id) === String(newCostCenter.id)) === -1) {
          setCostCenters((prev) => [...prev, newCostCenter]);
        }
      }
    }

    if (costCenterId === "") {
      await ConfirmService.show("Erro", "Centro de Custo inválido", true);
    }

    change({
      target: {
        name: "costCenterId",
        value: costCenterId,
      },
    });
  };

  // ============================================================
  // Handle Search
  // ============================================================
  const handleSearch = async () => {
    const item = await SearchHelpModal({
      urlData: "/Financial/Entries/SearchHelp",
      schema: await api.Financial.Entries.schema(),
    });

    if (item?.id) {
      load(item.id);
    }
  };

  // ============================================================
  // Criar baixa
  // ============================================================
  const handleAddClearing = async () => {
    if (!editing || !data.id || balanceAmount <= 0) return;

    const result = await ClearingModalService({
      documentId: data.id,
      currency: data.currency,
      balanceAmount,
    });

    if (!result?.documents?.length) return;

    for (const [index, document] of result.documents.entries()) {
      const amount = Number(document.allocatedAmount ?? document.balance ?? document.totalAmount ?? 0);
      if (amount <= 0) continue;

      const settlementResult = await api.Financial.Entries.settle(document.id, {
        clearingDocument: data.id,
        compensationDocumentId: data.id,
        clearingDate: result.clearingDate || today(),
        amount,
        interestAmount: index === 0 ? Number(result.interestAmount || 0) : 0,
        fineAmount: index === 0 ? Number(result.fineAmount || 0) : 0,
        discountAmount: index === 0 ? Number(result.discountAmount || 0) : 0,
        notes: result.notes || `Compensação com o documento ${data.id}`,
      });

      if (settlementResult?.errors) {
        return ConfirmService.show("Aviso", settlementResult.errors, true);
      }
    }

    await load(data.id);
    await ConfirmService.show("Aviso", "Documentos associados com sucesso!", true);
  };

  // ============================================================
  // Remover baixa
  // ============================================================

  const handleRemoveClearing = async (index) => {
    if (!editing) {
      return;
    }

    const confirmed = await ConfirmService.show("Confirmação", "Deseja remover esta baixa?", false);

    if (!confirmed) {
      return;
    }

    const clearing = data.clearings?.[index];
    if (clearing?.id) {
      const result = await api.Financial.Entries.deleteSettlement(data.id, clearing.id);
      if (result?.errors) return ConfirmService.show("Aviso", result.errors, true);

      return load(data.id);
    }

    setData((current) => ({
      ...current,

      clearings: (current.clearings || []).filter((_, i) => i !== index),
    }));
  };

  const handleRemoveAssociation = async (association) => {
    if (!editing) return;

    const confirmed = await ConfirmService.show("Confirmação", "Deseja remover esta associação?", false);
    if (!confirmed) return;

    const result = await api.Financial.Entries.deleteSettlement(data.id, association.id);
    if (result?.errors) return ConfirmService.show("Aviso", result.errors, true);

    await load(data.id);
  };

  // ============================================================
  // Handle Save
  // ============================================================
  const handleSave = async () => {
    if (!data.accountType || totalAmount <= 0) {
      return ConfirmService.show("Aviso", "Informe o tipo de conta e um valor total maior que zero.", true);
    }

    const result = isCreate
      ? await api.Financial.Entries.create({
          ...data,
          clearings: undefined,
          totalAmount,
        })
      : await api.Financial.Entries.update(data.id, {
          ...data,
          clearings: undefined,
          totalAmount,
        });

    if (result?.errors) {
      return ConfirmService.show("Aviso", result.errors, true);
    }

    const entryId = data.id || result.id;

    // Salvar baixas
    if (!isCreate) {
      for (const clearing of data.clearings || []) {
        if (!clearing.id) {
          const settlementResult = await api.Financial.Entries.settle(entryId, clearing);

          if (settlementResult?.errors) {
            return ConfirmService.show("Aviso", settlementResult.errors, true);
          }
        }
      }
    }

    await load(entryId);

    await ConfirmService.show("Aviso", "Registro salvo com sucesso!", true);
  };

  // ============================================================
  // Handle Create
  // ============================================================

  const handleCreate = async () => {
    setData({
      ...newDocument(),

      postingDate: today(),
      entryOrigin: "M",
      currency: "BRL",
    });

    setEditing(true);
    setIsCreate(true);

    commandAction({
      ...DEFAULT_ACTIONS,
      save: true,
    });
  };

  // ============================================================
  // Handle Change
  // ============================================================

  const handleChange = async () => {
    setEditing(true);
    setIsCreate(false);

    commandAction({
      ...DEFAULT_ACTIONS,
      save: true,
    });
  };

  // ============================================================
  // Handle Cancel
  // ============================================================

  const handleCancel = async () => {
    if (isCreate) {
      setData(newDocument());
      setEditing(false);

      commandAction({
        ...DEFAULT_ACTIONS,
        create: true,
      });
    } else {
      load(data.id);
    }
  };

  // ============================================================
  // Status
  // ============================================================
  const statusDescription = {
    OPEN: "Em aberto",
    PARTIAL: "Parcial",
    CLEARED: "Baixado",
    RECEIVED_AVAILABLE: "Recebido — disponível para distribuição",
    RECEIVED_PARTIAL: "Recebido — parcialmente distribuído",
    DISTRIBUTED: "Recebido — totalmente distribuído",
    PAID_AVAILABLE: "Pago — disponível para distribuição",
    PAID_PARTIAL: "Pago — parcialmente distribuído",
    PAID_DISTRIBUTED: "Pago — totalmente distribuído",
  };

  return (
    <Container className="mt-2">
      <h1 className="fw-light text-center">Documento financeiro</h1>

      <br />

      <Row className="g-3 align-items-start">
        <Col lg={9}>
          <Card className="mb-2">
            <Card.Header>
              <Row className="align-items-center">
                <Col>
                  <h5 className="mb-0">Documento financeiro</h5>
                </Col>

                <Col className="text-end">
                  <Button variant="success" size="sm" className="me-2" onClick={handleCreate} hidden={!actions.create}>
                    <FaPlus /> Novo
                  </Button>

                  <Button variant="primary" size="sm" className="me-2" onClick={handleChange} hidden={!actions.change}>
                    <FaPencilAlt /> Alterar
                  </Button>

                  <Button variant="danger" size="sm" className="me-2" onClick={handleCancel} hidden={!actions.save}>
                    <MdOutlineCancel /> Cancelar
                  </Button>

                  <Button variant="primary" size="sm" className="me-2" onClick={handleSave} hidden={!actions.save}>
                    <FaSave /> Salvar
                  </Button>
                </Col>
              </Row>
            </Card.Header>

            <Card.Body>
              <Row className="g-2 mb-2">
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Código</Form.Label>

                    <InputGroup>
                      <Form.Control type="text" size="sm" value={data.id === 0 ? "" : (data.id ?? "")} name="id" disabled={true} />

                      <InputGroup.Text size="sm" type="button" onClick={handleSearch}>
                        <FaSearch />
                      </InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                </Col>

                <Col md={2}>
                  <Form.Label>Tipo de Conta</Form.Label>

                  <Form.Select size="sm" name="accountType" value={data.accountType} onChange={change} disabled={!editing}>
                    <option value="">Selecione</option>

                    <option value="RE">(+) Receber</option>

                    <option value="PA">(-) Pagar</option>
                  </Form.Select>
                </Col>

                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Empresa/Filial</Form.Label>

                    <Form.Select size="sm" value={data.branchId || ""} name="branchId" onChange={change} disabled={!editing}>
                      <option value="">Selecione...</option>

                      {branches
                        ?.filter((branch) => !editing || branch.disabled === false)
                        ?.map((branch) => (
                          <option key={branch.id} value={branch.id}>
                            {branch.description}
                          </option>
                        ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={2}>
                  <Form.Label>Período</Form.Label>

                  <Form.Control size="sm" value={periodFrom(data.postingDate)} readOnly disabled />
                </Col>

                <Col md={2}>
                  <Form.Label>Lançamento</Form.Label>

                  <Form.Select size="sm" name="entryOrigin" value={data.entryOrigin || ""} onChange={change} disabled>
                    <option value="">Selecione</option>
                    <option value="M">Manual</option>
                    <option value="A">Automático</option>
                  </Form.Select>
                </Col>
              </Row>

              <Row className="g-2 mb-2">
                <Col md={4}>
                  <Form.Label>Documento</Form.Label>

                  <Form.Select size="sm" name="documentType" value={data.documentType || ""} onChange={change} disabled={!editing}>
                    <option value="">Selecione</option>
                    <option value="CL">Cliente</option>
                    <option value="VD">Fornecedor</option>
                    <option value="CC">Centro de custo</option>
                    <option value="EM">Funcionário</option>
                    <option value="OS">Ordem de Serviço</option>
                    <option value="OT">Outro</option>
                  </Form.Select>
                </Col>

                <Col md={4}>
                  <Form.Label>Nº do Documento</Form.Label>

                  <Form.Control
                    size="sm"
                    name="documentNumber"
                    value={data.documentNumber || ""}
                    onChange={change}
                    disabled={!editing}
                  />
                </Col>

                <Col md={4}>
                  <Form.Label>Referência</Form.Label>

                  <Form.Control
                    size="sm"
                    name="referenceKey"
                    value={data.referenceKey || ""}
                    onChange={change}
                    disabled={!editing}
                    placeholder="NF-e / Boleto / Fatura"
                  />
                </Col>
              </Row>

              <Row className="g-2 mb-2">
                <Col md={2}>
                  <Form.Label>Data Documento</Form.Label>

                  <Form.Control
                    size="sm"
                    type="date"
                    name="documentDate"
                    value={data.documentDate || ""}
                    onChange={change}
                    disabled={!editing}
                  />
                </Col>

                <Col md={2}>
                  <Form.Label>Data Lançamento</Form.Label>

                  <Form.Control
                    size="sm"
                    type="date"
                    name="postingDate"
                    value={data.postingDate || ""}
                    onChange={change}
                    disabled={!editing}
                  />
                </Col>

                <Col md={2}>
                  <Form.Label>Data Vencimento</Form.Label>

                  <Form.Control
                    size="sm"
                    type="date"
                    name="dueDate"
                    value={data.dueDate || ""}
                    onChange={change}
                    disabled={!editing}
                  />
                </Col>
                <Col md={4} hidden={data.accountType !== "RE"}>
                  <Form.Label>Documento de recebimento</Form.Label>
                  <Form.Check
                    type="switch"
                    name="isReceipt"
                    //label=""
                    checked={Boolean(data.isReceipt)}
                    onChange={change}
                    disabled={!editing}
                  />
                </Col>
                <Col md={4} hidden={data.accountType !== "PA"}>
                  <Form.Label>Documento de pagamento</Form.Label>
                  <Form.Check
                    type="switch"
                    name="isPayment"
                    checked={Boolean(data.isPayment)}
                    onChange={change}
                    disabled={!editing}
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Amounts */}
          <Card className="mb-2">
            <Card.Header>
              <Row className="align-items-center">
                <Col>
                  <h5 className="mb-0">Valores</h5>
                </Col>
              </Row>
            </Card.Header>

            <Card.Body>
              <Row className="g-2 mb-2">
                <Col md={3}>
                  <Form.Label>Valor Original</Form.Label>

                  <CurrencyInput
                    name="originalAmount"
                    value={data.originalAmount}
                    onChange={change}
                    disabled={!editing}
                    currency={data.currency}
                    showDisabled={true}
                  />
                </Col>

                <Col md={3}>
                  <Form.Label>Moeda</Form.Label>

                  <Form.Select size="sm" name="currency" value={data.currency || "BRL"} onChange={change} disabled>
                    <option value="BRL">Real (R$)</option>
                  </Form.Select>
                </Col>
              </Row>
              <Row className="g-2 mb-2">
                <Col md={3}>
                  <Form.Label>Valor Juros</Form.Label>

                  <CurrencyInput
                    name="interestAmount"
                    value={data.interestAmount}
                    onChange={change}
                    disabled={!editing}
                    currency={data.currency}
                    showDisabled={true}
                  />
                </Col>

                <Col md={3}>
                  <Form.Label>Valor Multa</Form.Label>

                  <CurrencyInput
                    name="fineAmount"
                    value={data.fineAmount}
                    onChange={change}
                    disabled={!editing}
                    currency={data.currency}
                    showDisabled={true}
                  />
                </Col>

                <Col md={3}>
                  <Form.Label>Valor Desconto</Form.Label>

                  <CurrencyInput
                    name="discountAmount"
                    value={data.discountAmount}
                    onChange={change}
                    disabled={!editing}
                    currency={data.currency}
                    showDisabled={true}
                  />
                </Col>

                <Col md={3}>
                  <Form.Label>Valor Total</Form.Label>

                  <Form.Control
                    size="sm"
                    value={totalAmount.toLocaleString("pt-BR", {
                      style: "currency",
                      currency: data.currency || "BRL",
                    })}
                    disabled
                    readOnly
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Classification */}
          <Card className="mb-2">
            <Card.Header>
              <Row className="align-items-center">
                <Col>
                  <h5 className="mb-0">Classificação</h5>
                </Col>
              </Row>
            </Card.Header>

            <Card.Body>
              <Row className="g-2 mb-2">
                <Col md={4}>
                  <Form.Label>Categoria Financeira</Form.Label>

                  <Form.Select size="sm" name="categoryId" value={data.categoryId || ""} onChange={change} disabled={!editing}>
                    <option value="">Selecione</option>

                    {(Array.isArray(categories) ? categories : [])
                      .filter((category) => !editing || !category.disabled)
                      .map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.description}
                        </option>
                      ))}
                  </Form.Select>
                </Col>

                <Col md={4}>
                  <Form.Label>Centro de Custo</Form.Label>

                  <InputGroup>
                    <Form.Control
                      type="text"
                      size="sm"
                      name="costCenterId"
                      value={data.costCenterId || ""}
                      disabled={!editing}
                      onChange={change}
                      onBlur={(e) => validateCostCenter(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();

                          validateCostCenter(e.target.value);
                        }
                      }}
                    />

                    <InputGroup.Text size="sm" type="button" onClick={handleSearchCostCenter}>
                      <FaSearch />
                    </InputGroup.Text>
                  </InputGroup>
                </Col>

                <Col md={4}>
                  <Form.Label>Descrição</Form.Label>

                  <Form.Control
                    type="text"
                    size="sm"
                    value={costCenters.find((x) => x.id === data.costCenterId)?.description || ""}
                    disabled
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Additional */}
          <Card className="mb-2">
            <Card.Header>
              <Row className="align-items-center">
                <Col>
                  <h5 className="mb-0">Informações Adicionais</h5>
                </Col>
              </Row>
            </Card.Header>

            <Card.Body>
              <Row className="g-2 mb-2">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Descrição / Observação</Form.Label>

                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={data.notes || ""}
                      maxLength={150}
                      name="notes"
                      onChange={change}
                      disabled={!editing}
                      style={{
                        resize: "none",
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Clearing */}
          <Card className="mb-2">
            <Card.Header>
              <Row className="align-items-center">
                <Col>
                  <h5 className="mb-0">Baixas / Compensações</h5>
                </Col>

                <Col className="text-end">
                  <Button
                    variant="success"
                    size="sm"
                    onClick={handleAddClearing}
                    disabled={!editing || !data.id || balanceAmount <= 0}
                  >
                    <FaPlus /> Nova Baixa
                  </Button>
                </Col>
              </Row>
            </Card.Header>

            <Card.Body>
              <Tabs defaultActiveKey="clearings" id="financial-compensation-tabs" className="mb-3">
                <Tab eventKey="clearings" title="Compensações realizadas">
                  {(data.clearings ?? []).length === 0 ? (
                    <div className="text-muted text-center py-3">Nenhuma baixa registrada.</div>
                  ) : (
                    <Table striped bordered hover size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th>Documento</th>
                          <th>Data Documento</th>
                          <th>Data Compensação</th>
                          <th className="text-end">Valor</th>
                          <th className="text-center">Ação</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data.clearings ?? []).map((clearing, index) => (
                          <tr key={clearing.id || index}>
                            <td className="text-center">{clearing.clearingDocument}</td>
                            <td className="text-center">
                              {clearing.clearingDocumentDate
                                ? new Date(clearing.clearingDocumentDate).toLocaleDateString("pt-BR")
                                : "-"}
                            </td>
                            <td className="text-center">
                              {clearing.clearingDate ? new Date(clearing.clearingDate).toLocaleDateString("pt-BR") : "-"}
                            </td>
                            <td className="text-end">
                              {Number(clearing.totalAmount || clearing.amount || 0).toLocaleString("pt-BR", {
                                style: "currency",
                                currency: data.currency || "BRL",
                              })}
                            </td>
                            <td className="text-center">
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleRemoveClearing(index)}
                                disabled={!editing}
                                title="Remover baixa"
                              >
                                <FaTrash />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Tab>

                <Tab eventKey="associated" title="Associado a outros documentos">
                  {(data.compensatedParents || []).length === 0 ? (
                    <div className="text-muted text-center py-3">Este documento ainda não foi associado a outro título.</div>
                  ) : (
                    <Table striped bordered hover size="sm" className="mb-0">
                      <thead>
                        <tr className="text-center align-middle">
                          <th>Documento pai</th>
                          <th>Número</th>
                          <th>Referência</th>
                          <th>Compensação</th>
                          <th>Valor compensado</th>
                          <th>Motivo / observação</th>
                          <th>Ação</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.compensatedParents.map((parent) => (
                          <tr key={parent.id}>
                            <td>{parent.financialEntryId}</td>
                            <td>{parent.documentNumber || "-"}</td>
                            <td>{parent.referenceKey || "-"}</td>
                            <td className="text-center">
                              {parent.clearingDate ? new Date(parent.clearingDate).toLocaleDateString("pt-BR") : "-"}
                            </td>
                            <td className="text-end">
                              {Number(parent.totalAmount || 0).toLocaleString("pt-BR", {
                                style: "currency",
                                currency: data.currency || "BRL",
                              })}
                            </td>
                            <td>{parent.notes || "-"}</td>
                            <td className="text-center">
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleRemoveAssociation(parent)}
                                disabled={!editing}
                                title="Remover associação"
                              >
                                <FaTrash />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3}>
          <div className="position-sticky" style={{ top: "1rem" }}>
            <Card className="mb-3">
              <Card.Header>
                <h5 className="mb-0 text-primary">Resumo financeiro</h5>
              </Card.Header>
              <Card.Body>
                <div className="d-flex justify-content-between ">
                  <span>Valor original</span>
                  <span>
                    {Number(data.originalAmount || 0).toLocaleString("pt-BR", {
                      style: "currency",
                      currency: data.currency || "BRL",
                    })}
                  </span>
                </div>

                <div className="d-flex justify-content-between text-warning">
                  <span>
                    (
                    <span className="d-inline-block text-center" style={{ width: "12px" }}>
                      +
                    </span>
                    ) Juros
                  </span>
                  <span className="text-warning">
                    {Number(data.interestAmount || 0).toLocaleString("pt-BR", {
                      style: "currency",
                      currency: data.currency || "BRL",
                    })}
                  </span>
                </div>
                <div className="d-flex justify-content-between text-danger">
                  <span>
                    (
                    <span className="d-inline-block text-center" style={{ width: "12px" }}>
                      +
                    </span>
                    ) Multa
                  </span>

                  <span>
                    {Number(data.fineAmount || 0).toLocaleString("pt-BR", {
                      style: "currency",
                      currency: data.currency || "BRL",
                    })}
                  </span>
                </div>

                <div className="d-flex justify-content-between text-success">
                  <span>
                    (
                    <span className="d-inline-block text-center" style={{ width: "12px" }}>
                      -
                    </span>
                    ) Desconto
                  </span>

                  <span>
                    {Number(data.discountAmount || 0).toLocaleString("pt-BR", {
                      style: "currency",
                      currency: data.currency || "BRL",
                    })}
                  </span>
                </div>
                <hr />
                <div className="d-flex justify-content-between align-items-center text-primary fw-bold">
                  <span>Valor total</span>
                  <span>{totalAmount.toLocaleString("pt-BR", { style: "currency", currency: data.currency || "BRL" })}</span>
                </div>
              </Card.Body>
            </Card>

            {(data.isReceipt || data.isPayment) && (
              <Card className="mb-3">
                <Card.Header>
                  <h5 className="mb-0 text-primary">
                    {data.isPayment ? "Confirmação de pagamento" : "Confirmação de recebimento"}
                  </h5>
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-2">
                    <Form.Label>{data.isPayment ? "Data do pagamento" : "Data do recebimento"}</Form.Label>
                    <Form.Control
                      size="sm"
                      type="date"
                      name={data.isPayment ? "paidOn" : "receivedOn"}
                      value={data.isPayment ? data.paidOn || "" : data.receivedOn || ""}
                      onChange={change}
                      disabled={!editing}
                    />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>Conta / banco</Form.Label>
                    <Form.Control
                      size="sm"
                      name={data.isPayment ? "paymentAccount" : "receiptAccount"}
                      value={data.isPayment ? data.paymentAccount || "" : data.receiptAccount || ""}
                      onChange={change}
                      disabled={!editing}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Comprovante / referência</Form.Label>
                    <Form.Control
                      size="sm"
                      name={data.isPayment ? "paymentReference" : "receiptReference"}
                      value={data.isPayment ? data.paymentReference || "" : data.receiptReference || ""}
                      onChange={change}
                      disabled={!editing}
                    />
                  </Form.Group>
                </Card.Body>
              </Card>
            )}

            <Card className="mb-3">
              <Card.Header>
                <h5 className="mb-0 text-primary">Situação do documento</h5>
              </Card.Header>
              <Card.Body>
                <div className="d-flex justify-content-between mb-2">
                  <span>Compensações</span>
                  <strong>{clearedAmount.toLocaleString("pt-BR", { style: "currency", currency: data.currency || "BRL" })}</strong>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Associados</span>
                  <strong>
                    {associatedAmount.toLocaleString("pt-BR", { style: "currency", currency: data.currency || "BRL" })}
                  </strong>
                </div>
                <div className="d-flex justify-content-between mb-3">
                  <span>Saldo</span>
                  <strong className={balanceAmount > 0 ? "text-danger" : "text-success"}>
                    {balanceAmount.toLocaleString("pt-BR", { style: "currency", currency: data.currency || "BRL" })}
                  </strong>
                </div>

                <div
                  className={`text-center rounded py-2 fw-bold ${["CLEARED", "DISTRIBUTED", "PAID_DISTRIBUTED"].includes(financialStatus) ? "bg-success-subtle text-success" : "bg-warning-subtle text-warning-emphasis"}`}
                >
                  {statusDescription[financialStatus]}
                </div>
              </Card.Body>
            </Card>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default FinancialEntry;
